import React, { useState, useEffect } from "react";
import {
  Button,
  Textarea,
  Text,
  Stack,
  FileButton,
  Group,
  Box,
  Card,
  Title,
  Alert,
  Badge,
  Paper,
  Container,
} from "@mantine/core";
import { PdfDropzone } from "./PdfDropzone";
import {
  IconCheck,
  IconX,
  IconAlertCircle,
  IconAlertTriangle,
} from "@tabler/icons-react";
// @ts-ignore
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import { PDFDocument } from "pdf-lib";
import { sha256Hex } from "../lib/hash";
import { reconstructDocument } from "../lib/pdf-build";
import type {
  SigningMetadata,
  ServerAttestation,
} from "../lib/signing-metadata";
import { computeCreatorSigningInput } from "../lib/signing-metadata";
import { verifyData, getStoredKeys } from "../lib/keys";
import { verifyServerSignature } from "../lib/server-keys";
import { lookupByKeyId } from "../lib/key-server";

// Ensure worker is set
pdfjsLib.GlobalWorkerOptions.workerSrc = "./pdf.worker.js";

type ValidateDocumentViewProps = {
  initialFile?: File | null;
  onFileLoaded?: () => void;
};

export function ValidateDocumentView({
  initialFile,
  onFileLoaded,
}: ValidateDocumentViewProps = {}) {
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [metadata, setMetadata] = useState<SigningMetadata | null>(null);
  const [result, setResult] = useState<{
    documentHash?: string;
    recomputedHash?: string;
    hashMatch?: boolean;
    signatures: {
      email: string;
      keyId: string;
      timestamp: string;
      localValid: boolean;
      embeddedValid: boolean;
      localError?: string;
      serverSignature?: ServerAttestation;
      serverSignatureValid?: boolean;
      publicKey?: string;
      registryVerified?: boolean;
    }[];
    creatorStatus?: {
      keyId: string;
      valid: boolean;
      embeddedValid: boolean;
      error?: string;
      serverSignature?: ServerAttestation;
      serverSignatureValid?: boolean;
      publicKey?: string;
      registryVerified?: boolean;
    };
  } | null>(null);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoValidated, setAutoValidated] = useState(false);

  const [originalPdfBuffer, setOriginalPdfBuffer] = useState<ArrayBuffer | null>(null);
  const [reconstructing, setReconstructing] = useState(false);
  const [reconstructedPdfUrl, setReconstructedPdfUrl] = useState<string | null>(null);

  const handlePdfChange = async (file: File | null) => {
    setPdfFile(file || null);
    setResult(null);
    setError(null);
    setMetadata(null);
    setAutoValidated(false);
    setOriginalPdfBuffer(null);
    setReconstructedPdfUrl(null);

    if (file) {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer.slice(0) })
          .promise;
        const attachments = await pdf.getAttachments();

        if (attachments && attachments["signing-metadata.json"]) {
          const content = attachments["signing-metadata.json"].content;
          const decoder = new TextDecoder();
          const jsonStr = decoder.decode(content);
          const meta = JSON.parse(jsonStr) as SigningMetadata;
          setMetadata(meta);

          // Auto-validate if metadata is present
          setTimeout(() => {
            handleValidate(meta, arrayBuffer);
          }, 100);
        } else {
          setError(
            'No "signing-metadata.json" attachment found in this PDF. This may not be a Signed+ document.',
          );
        }
      } catch (e) {
        console.error(e);
        setError(
          "Failed to parse PDF: " +
          (e instanceof Error ? e.message : String(e)),
        );
      }
    }
  };

  const handleValidate = async (
    meta?: SigningMetadata,
    arrayBuffer?: ArrayBuffer,
  ) => {
    const metadataToUse = meta || metadata;
    if (!metadataToUse) {
      setError("No metadata available to validate");
      return;
    }

    const bufferToUse =
      arrayBuffer || (pdfFile ? await pdfFile.arrayBuffer() : null);
    if (!bufferToUse) {
      setError("No PDF file available");
      return;
    }

    setValidating(true);
    setResult(null);
    setError(null);

    try {
      // Try to get original.pdf attachment for hash verification
      const pdf = await pdfjsLib.getDocument({ data: bufferToUse.slice(0) })
        .promise;
      const attachments = await pdf.getAttachments();
      const originalFile = attachments ? attachments["original.pdf"] : null;

      let recomputedHash: string | undefined;
      if (originalFile) {
        // Use original.pdf for hash verification (before attachments were added)
        recomputedHash = await sha256Hex(originalFile.content);
        setOriginalPdfBuffer(originalFile.content.slice(0));
      } else {
        setOriginalPdfBuffer(null);
      }
      // Note: If original.pdf is not available, we cannot verify the hash
      // because the current file includes attachments which change the hash

      // Get stored keys to try matching by keyId
      const storedKeys = getStoredKeys();
      const keyMap = new Map<string, string>();
      storedKeys.forEach((key) => {
        keyMap.set(key.id.toUpperCase(), key.publicKeyArmored);
      });

      // Validate creator signature
      let creatorStatus:
        | {
          keyId: string;
          valid: boolean;
          embeddedValid: boolean;
          error?: string;
          serverSignature?: ServerAttestation;
          serverSignatureValid?: boolean;
          publicKey?: string;
          registryVerified?: boolean;
        }
        | undefined;
      let creatorRegistryVerified = false;

      if (metadataToUse.creatorSignature) {
        const creatorKeyId = metadataToUse.creatorSignature.keyId.toUpperCase();
        let creatorPublicKey = keyMap.get(creatorKeyId);

        // Registry Lookup (Courtesy Check)
        try {
          const registryKey = await lookupByKeyId(creatorKeyId);
          if (registryKey) {
            creatorRegistryVerified = true;
            // We can optionally use this key if we didn't have it locally or embedded!
            if (
              !creatorPublicKey &&
              !metadataToUse.creatorSignature.publicKey
            ) {
              creatorPublicKey = registryKey;
            }
          }
        } catch (e) {
          /* ignore */
        }

        if (creatorPublicKey) {
          try {
            // v2 signs the full metadata (hash + signers + fields), v1 signed just the hash
            const creatorInput =
              metadataToUse.version >= 2
                ? computeCreatorSigningInput(
                  metadataToUse.documentHash,
                  metadataToUse.signers,
                  metadataToUse.fields,
                )
                : metadataToUse.documentHash;
            const valid = await verifyData(
              creatorInput,
              metadataToUse.creatorSignature.signature,
              creatorPublicKey,
            );
            creatorStatus = {
              keyId: metadataToUse.creatorSignature.keyId,
              valid,
              embeddedValid: false,
              serverSignature: metadataToUse.creatorSignature.serverSignature,
              serverSignatureValid: false,
              publicKey: creatorPublicKey,
            };
          } catch (e) {
            creatorStatus = {
              keyId: metadataToUse.creatorSignature.keyId,
              valid: false,
              embeddedValid: false,
              error:
                "Verification failed: " +
                (e instanceof Error ? e.message : String(e)),
              publicKey: creatorPublicKey,
            };
          }
        } else if (metadataToUse.creatorSignature.publicKey) {
          try {
            const creatorInput =
              metadataToUse.version >= 2
                ? computeCreatorSigningInput(
                  metadataToUse.documentHash,
                  metadataToUse.signers,
                  metadataToUse.fields,
                )
                : metadataToUse.documentHash;
            const embeddedValid = await verifyData(
              creatorInput,
              metadataToUse.creatorSignature.signature,
              metadataToUse.creatorSignature.publicKey,
            );
            creatorStatus = {
              keyId: metadataToUse.creatorSignature.keyId,
              valid: false,
              embeddedValid,
              serverSignature: metadataToUse.creatorSignature.serverSignature,
              serverSignatureValid: false,
              publicKey: metadataToUse.creatorSignature.publicKey,
            };
          } catch (e) {
            creatorStatus = {
              keyId: metadataToUse.creatorSignature.keyId,
              valid: false,
              embeddedValid: false,
              error:
                "Embedded Verification failed: " +
                (e instanceof Error ? e.message : String(e)),
              publicKey: metadataToUse.creatorSignature.publicKey,
            };
          }
        } else {
          creatorStatus = {
            keyId: metadataToUse.creatorSignature.keyId,
            valid: false,
            embeddedValid: false,
            error: `Public key not found Locally, Embedded or in Registry. Key ID: ${metadataToUse.creatorSignature.keyId}.`,
          };
        }
      }

      if (creatorStatus) {
        creatorStatus.registryVerified = creatorRegistryVerified;
      }

      // Validate all signatures
      const signatures = await Promise.all(
        (metadataToUse.signatures || []).map(async (sig) => {
          const sigKeyId = sig.keyId.toUpperCase();
          let sigPublicKey = keyMap.get(sigKeyId);
          const inputString = `${metadataToUse.documentHash}${sig.email}${sig.timestamp}`;

          let localValid = false;
          let embeddedValid = false;
          let localError = `Public key not found Locally, Embedded or in Registry. Key ID: ${sig.keyId}.`;
          let registryVerified = false;

          // Registry Lookup (Courtesy Check)
          try {
            const registryKey = await lookupByKeyId(sigKeyId);
            if (registryKey) {
              registryVerified = true;
              if (!sigPublicKey && !sig.publicKey) {
                sigPublicKey = registryKey;
              }
            }
          } catch (e) {
            /* ignore */
          }

          if (sigPublicKey) {
            try {
              localValid = await verifyData(
                inputString,
                sig.signature,
                sigPublicKey,
              );
              localError = "";
            } catch (e) {
              localValid = false;
              localError =
                "Local PGP Verification failed: " +
                (e instanceof Error ? e.message : String(e));
            }
          } else if (sig.publicKey) {
            try {
              embeddedValid = await verifyData(
                inputString,
                sig.signature,
                sig.publicKey,
              );
              localError = "";
            } catch (e) {
              embeddedValid = false;
              localError =
                "Embedded PGP Verification failed: " +
                (e instanceof Error ? e.message : String(e));
            }
          }

          // A signature can be considered "valid" if either local PGP works OR server metadata exists.
          // The UI will show tiers.
          return {
            email: sig.email,
            keyId: sig.keyId,
            timestamp: sig.timestamp,
            localValid,
            embeddedValid,
            localError,
            serverSignature: sig.serverSignature,
            serverSignatureValid: false, // will be verified below
            publicKey: sigPublicKey || sig.publicKey,
            registryVerified,
          };
        }),
      );

      // Verify all server signatures cryptographically (ECDSA P-256)
      for (const sig of signatures) {
        if (
          sig.serverSignature?.signature &&
          sig.serverSignature?.serverKeyId
        ) {
          const { signature: sigValue, ...payloadToVerify } =
            sig.serverSignature;
          sig.serverSignatureValid = await verifyServerSignature(
            payloadToVerify,
            sigValue,
            sig.serverSignature.serverKeyId,
          );
        }
      }

      // Also verify creator's server signature
      if (
        creatorStatus?.serverSignature?.signature &&
        creatorStatus.serverSignature.serverKeyId
      ) {
        const { signature: sigValue, ...payloadToVerify } =
          creatorStatus.serverSignature;
        creatorStatus.serverSignatureValid = await verifyServerSignature(
          payloadToVerify,
          sigValue,
          creatorStatus.serverSignature.serverKeyId,
        );
      }

      setResult({
        documentHash: metadataToUse.documentHash,
        recomputedHash,
        hashMatch: recomputedHash
          ? metadataToUse.documentHash === recomputedHash
          : undefined,
        signatures,
        creatorStatus,
      });
      setAutoValidated(true);
    } catch (e) {
      console.error(e);
      setError(
        "Validation failed: " + (e instanceof Error ? e.message : String(e)),
      );
      setResult({ signatures: [] } as any);
    } finally {
      setValidating(false);
    }
  };

  // Load initial file if provided
  useEffect(() => {
    if (initialFile && !pdfFile) {
      handlePdfChange(initialFile).then(() => {
        if (onFileLoaded) {
          onFileLoaded();
        }
      });
    }
  }, [initialFile]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReconstruct = async () => {
    if (!originalPdfBuffer || !metadata) return;
    setReconstructing(true);
    try {
      const pdfBytes = await reconstructDocument(originalPdfBuffer, metadata, pdfFile?.name);
      const blob = new Blob([pdfBytes as any], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setReconstructedPdfUrl(url);

      const a = document.createElement("a");
      a.href = url;
      a.download = `${pdfFile?.name.replace(".pdf", "")}-verified-pristine.pdf`;
      a.click();
    } catch (err) {
      console.error(err);
      alert("Failed to rebuild document: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setReconstructing(false);
    }
  };

  // For overall document validity, we consider it "valid" if all signatures have at least ONE form of proof
  const allSignaturesValid =
    result &&
    result.signatures.length > 0 &&
    result.signatures.every(
      (s) => s.localValid || s.embeddedValid || s.serverSignature,
    ) &&
    (!result.creatorStatus ||
      result.creatorStatus.valid ||
      result.creatorStatus.embeddedValid ||
      result.creatorStatus.serverSignature);
  const someSignaturesInvalid =
    result &&
    (result.signatures.some(
      (s) => !s.localValid && !s.embeddedValid && !s.serverSignature,
    ) ||
      (result.creatorStatus &&
        !result.creatorStatus.valid &&
        !result.creatorStatus.embeddedValid &&
        !result.creatorStatus.serverSignature));

  return !pdfFile ? (
    <div className="full-center-frame">
      <Box style={{ width: "100%", maxWidth: 800 }}>
        <div className="premium-wrapper">
          <Title order={2} mb="lg">
            Validate Document
          </Title>
          <Text c="dimmed" mb="lg">
            Upload a Signed+ document to verify its signatures and integrity.
            The document will be automatically validated if metadata is present.
          </Text>
          <PdfDropzone
            onFileSelected={handlePdfChange}
            label="Drag PDF here or click to select"
            description="Upload a Signed+ document to verify its signatures and integrity"
          />
        </div>
      </Box>
    </div>
  ) : (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={2} mb="xs">
            Validate Document
          </Title>
          <Text c="dimmed">
            Upload a Signed+ document to verify its signatures and integrity.
            The document will be automatically validated if metadata is present.
          </Text>
        </div>

        <Paper p="md" withBorder>
          <Group justify="space-between">
            <div>
              <Text fw={600}>File: {pdfFile.name}</Text>
              {metadata && (
                <Text size="sm" c="dimmed" mt={4}>
                  Created: {new Date(metadata.createdAt).toLocaleString()} •{" "}
                  {metadata.signers.length} signer
                  {metadata.signers.length !== 1 ? "s" : ""}
                </Text>
              )}
            </div>
            <Button variant="light" onClick={() => handlePdfChange(null)}>
              Change File
            </Button>
          </Group>
        </Paper>

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
            {error}
          </Alert>
        )}

        {validating && (
          <Alert title="Validating..." color="blue">
            Verifying signatures and document integrity...
          </Alert>
        )}

        {result && (
          <Stack gap="md">
            {/* Overall Status */}
            {allSignaturesValid && result.hashMatch !== false && (
              <Alert
                icon={<IconCheck size={16} />}
                title="Document Valid"
                color="green"
              >
                All signatures are valid and the document integrity is
                confirmed.
              </Alert>
            )}
            {someSignaturesInvalid && (
              <Alert
                icon={<IconX size={16} />}
                title="Validation Issues"
                color="orange"
              >
                Some signatures could not be verified. Check the details below.
              </Alert>
            )}
            {result.hashMatch === false && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Document Modified"
                color="red"
              >
                The document hash does not match. The document may have been
                modified after signing.
              </Alert>
            )}
            {result.hashMatch === undefined && (
              <Alert
                icon={<IconAlertTriangle size={16} />}
                title="Integrity Check Unavailable"
                color="yellow"
              >
                The original document (original.pdf) is not embedded in this
                file. Document integrity cannot be independently verified — only
                signature checks are available.
              </Alert>
            )}

            {/* Document Hash */}
            <Card withBorder p="md">
              <Text fw={700} size="sm" mb="xs">
                Document Hash
              </Text>
              <Stack gap="xs">
                <div>
                  <Text size="xs" c="dimmed">
                    Stored Hash:
                  </Text>
                  <Text size="sm" ff="monospace">
                    {result.documentHash}
                  </Text>
                </div>
                {result.recomputedHash ? (
                  <>
                    <div>
                      <Text size="xs" c="dimmed">
                        Computed Hash:
                      </Text>
                      <Text size="sm" ff="monospace">
                        {result.recomputedHash}
                      </Text>
                    </div>
                    {result.hashMatch !== undefined && (
                      <Badge
                        color={result.hashMatch ? "green" : "red"}
                        variant="light"
                      >
                        {result.hashMatch
                          ? "✓ Hash Matches"
                          : "✗ Hash Mismatch"}
                      </Badge>
                    )}
                  </>
                ) : (
                  <Alert
                    icon={<IconAlertTriangle size={14} />}
                    color="yellow"
                    p="xs"
                  >
                    <Text size="xs" fw={600}>
                      ⚠ Original document not embedded
                    </Text>
                    <Text size="xs" mt={2}>
                      Document integrity cannot be independently verified. The
                      original.pdf attachment is missing, which means the
                      document content may have been altered after signing
                      without detection.
                    </Text>
                  </Alert>
                )}
              </Stack>
            </Card>

            {/* Creator Signature */}
            {result.creatorStatus && (
              <Card withBorder p="md">
                <Text fw={700} size="sm" mb="xs">
                  Creator Signature
                </Text>
                <Stack gap="xs">
                  <Group gap="xs">
                    <Badge
                      color={
                        result.creatorStatus.valid ||
                          result.creatorStatus.embeddedValid
                          ? "green"
                          : "red"
                      }
                      variant="light"
                    >
                      {result.creatorStatus.valid ||
                        result.creatorStatus.embeddedValid
                        ? "✓ Valid"
                        : "✗ Invalid"}
                    </Badge>
                    <Text size="sm" ff="monospace">
                      {result.creatorStatus.keyId}
                    </Text>
                  </Group>

                  {result.creatorStatus.valid ? (
                    <Text size="xs" c="green.9" fw={600}>
                      ✓ Independently Verified (Local Key Match)
                    </Text>
                  ) : result.creatorStatus.embeddedValid ? (
                    <Box
                      p="xs"
                      style={{
                        backgroundColor: "var(--mantine-color-yellow-0)",
                        borderRadius: 4,
                      }}
                    >
                      <Group gap={6}>
                        <IconAlertTriangle
                          size={14}
                          color="var(--mantine-color-yellow-7)"
                        />
                        <Text size="xs" c="yellow.9" fw={600}>
                          Self-Declared Identity (Embedded Key)
                        </Text>
                      </Group>
                      <Text size="10px" c="yellow.8" mt={2}>
                        Signature is cryptographically valid, but the signer's
                        identity has not been independently confirmed. The key
                        was provided by the signer themselves.
                      </Text>
                    </Box>
                  ) : result.creatorStatus.error ? (
                    <Text size="sm" c="red">
                      {result.creatorStatus.error}
                    </Text>
                  ) : null}

                  {result.creatorStatus.serverSignature && (
                    <Box
                      p="xs"
                      style={{
                        backgroundColor: result.creatorStatus
                          .serverSignatureValid
                          ? "var(--mantine-color-blue-0)"
                          : "var(--mantine-color-red-0)",
                        borderRadius: 4,
                      }}
                    >
                      <Text
                        size="xs"
                        c={
                          result.creatorStatus.serverSignatureValid
                            ? "blue.9"
                            : "red.9"
                        }
                        fw={600}
                      >
                        {result.creatorStatus.serverSignatureValid
                          ? "✓ Server Signature Verified (ECDSA)"
                          : "✗ Server Signature Invalid"}
                      </Text>
                      <Text
                        size="xs"
                        c={
                          result.creatorStatus.serverSignatureValid
                            ? "blue.8"
                            : "red.8"
                        }
                      >
                        Attested at{" "}
                        {result.creatorStatus.serverSignature.location} (
                        {result.creatorStatus.serverSignature.ip})
                      </Text>
                    </Box>
                  )}
                </Stack>
              </Card>
            )}

            {/* Signatures */}
            {result.signatures.length > 0 && (
              <Card withBorder p="md">
                <Text fw={700} size="sm" mb="md">
                  Signer Signatures ({result.signatures.length})
                </Text>
                <Stack gap="md">
                  {result.signatures.map((sig, i) => {
                    const hasAnyProof =
                      sig.localValid ||
                      sig.embeddedValid ||
                      sig.serverSignature;

                    return (
                      <Box
                        key={i}
                        p="sm"
                        style={{
                          border: "1px solid var(--mantine-color-gray-3)",
                          borderRadius: 4,
                        }}
                      >
                        <Stack gap="xs">
                          <Group gap="xs" justify="space-between">
                            <Group gap="xs">
                              <Badge
                                color={hasAnyProof ? "green" : "red"}
                                variant="light"
                              >
                                {hasAnyProof ? "✓ Verified" : "✗ Invalid"}
                              </Badge>
                              <Text fw={600}>{sig.email}</Text>
                            </Group>
                            <Text size="xs" c="dimmed">
                              {new Date(sig.timestamp).toLocaleString()}
                            </Text>
                          </Group>

                          <Text size="xs" ff="monospace" c="dimmed">
                            Key ID: {sig.keyId}
                          </Text>

                          <Stack gap={4} mt={4}>
                            {/* Tier 1 & 2: Local & Embedded Crypto */}
                            {sig.localValid ? (
                              <Box
                                p="xs"
                                style={{
                                  backgroundColor:
                                    "var(--mantine-color-green-0)",
                                  borderRadius: 4,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                <IconCheck
                                  size={14}
                                  color="var(--mantine-color-green-7)"
                                />
                                <Text size="xs" c="green.9" fw={600}>
                                  ✓ Independently Verified (Local Key Match)
                                </Text>
                              </Box>
                            ) : sig.embeddedValid ? (
                              <Box
                                p="xs"
                                style={{
                                  backgroundColor:
                                    "var(--mantine-color-yellow-0)",
                                  borderRadius: 4,
                                }}
                              >
                                <Group gap={6}>
                                  <IconAlertTriangle
                                    size={14}
                                    color="var(--mantine-color-yellow-7)"
                                  />
                                  <Text size="xs" c="yellow.9" fw={600}>
                                    Self-Declared Identity (Embedded Key)
                                  </Text>
                                </Group>
                                <Text size="10px" c="yellow.8" mt={2}>
                                  Signature is cryptographically valid, but
                                  identity not independently confirmed.
                                </Text>
                              </Box>
                            ) : (
                              <Box
                                p="xs"
                                style={{
                                  backgroundColor:
                                    "var(--mantine-color-gray-0)",
                                  borderRadius: 4,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                <IconAlertCircle
                                  size={14}
                                  color="var(--mantine-color-gray-5)"
                                />
                                <Text size="xs" c="dimmed">
                                  {sig.localError}
                                </Text>
                              </Box>
                            )}

                            {/* Tier 3: Server Audit Trail */}
                            {sig.serverSignature && (
                              <Box
                                p="xs"
                                style={{
                                  backgroundColor: sig.serverSignatureValid
                                    ? "var(--mantine-color-blue-0)"
                                    : "var(--mantine-color-red-0)",
                                  borderRadius: 4,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                {sig.serverSignatureValid ? (
                                  <IconCheck
                                    size={14}
                                    color="var(--mantine-color-blue-7)"
                                  />
                                ) : (
                                  <IconX
                                    size={14}
                                    color="var(--mantine-color-red-7)"
                                  />
                                )}
                                <Box>
                                  <Text
                                    size="xs"
                                    c={
                                      sig.serverSignatureValid
                                        ? "blue.9"
                                        : "red.9"
                                    }
                                    fw={600}
                                  >
                                    {sig.serverSignatureValid
                                      ? "✓ Server Signature Verified (ECDSA)"
                                      : "✗ Server Signature Invalid"}
                                  </Text>
                                  <Text
                                    size="xs"
                                    c={
                                      sig.serverSignatureValid
                                        ? "blue.8"
                                        : "red.8"
                                    }
                                  >
                                    Attested at {sig.serverSignature.location} (
                                    {sig.serverSignature.ip})
                                  </Text>
                                </Box>
                              </Box>
                            )}

                            {/* Tier 4: Public Keyserver Registry */}
                            {sig.registryVerified ? (
                              <Box
                                p="xs"
                                style={{
                                  backgroundColor:
                                    "var(--mantine-color-indigo-0)",
                                  borderRadius: 4,
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 4,
                                }}
                              >
                                <Group gap={8}>
                                  <IconCheck
                                    size={14}
                                    color="var(--mantine-color-indigo-7)"
                                  />
                                  <Text size="xs" c="indigo.9" fw={600}>
                                    ✓ Public Registry Verified (keys.openpgp.org)
                                  </Text>
                                </Group>
                                {sig.serverSignature?.registryFingerprint && (
                                  <Text size="10px" c="indigo.9" pl={22} ff="monospace">
                                    FP: {sig.serverSignature.registryFingerprint}
                                  </Text>
                                )}
                              </Box>
                            ) : (
                              <Box
                                p="xs"
                                style={{
                                  backgroundColor:
                                    "var(--mantine-color-gray-0)",
                                  borderRadius: 4,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                <IconAlertCircle
                                  size={14}
                                  color="var(--mantine-color-gray-5)"
                                />
                                <Text size="xs" c="dimmed">
                                  Not found in public registry
                                </Text>
                              </Box>
                            )}
                          </Stack>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              </Card>
            )}

            {/* Anti-Shadow Attack PDF Reconstruction */}
            {result && result.hashMatch && originalPdfBuffer && (
              <Card withBorder padding="md" radius="md" bg="blue.0">
                <Stack>
                  <div>
                    <Text fw={600} c="blue.9">
                      🛡️ Verified Safe Document View
                    </Text>
                    <Text size="sm" c="blue.8">
                      To prevent "Shadow Attacks" where a maliciously altered PDF container is swapped in after signing,
                      you can download a 100% freshly rebuilt version of this document constructed only from cryptographically validated data.
                    </Text>
                  </div>
                  <Button
                    onClick={handleReconstruct}
                    loading={reconstructing}
                    variant="filled"
                    color="blue"
                  >
                    Download Rebuilt Authoritative PDF
                  </Button>
                </Stack>
              </Card>
            )}

            {/* Re-validate button */}
            <Button
              onClick={() => handleValidate()}
              loading={validating}
              variant="light"
            >
              Re-validate Document
            </Button>
          </Stack>
        )}

        {metadata && !result && !validating && (
          <Button
            onClick={() => handleValidate()}
            loading={validating}
            size="lg"
          >
            Validate Document
          </Button>
        )}
      </Stack>
    </Container>
  );
}
